import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  collection, query, where, onSnapshot, doc, deleteField, getDoc,
  getDocs, writeBatch, Timestamp, arrayUnion, arrayRemove,
} from 'firebase/firestore';
import { setDoc, updateDoc, deleteDoc, confirmarLote } from '../config/escrituras';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';
import { GastoPlan, InvitacionPlan, PagoPlan, ParticipantePlan, Plan } from '../types';
import { usePerfil } from './PerfilContext';
import { nombreDeUsuario } from '../utils/usuario';
import { PLAZO_POR_DEFECTO } from '../utils/recordatoriosAMedias';
import { comprobarLimite } from '../config/planes';
import { planesOrganizados } from '../utils/limites';

/**
 * Datos del módulo A medias: planes de gastos compartidos.
 *
 * El contexto solo escucha la lista de planes. Los gastos y pagos de un plan se
 * cargan con useMovimientosPlan() desde la página que los necesita, para no
 * tener abiertas las subcolecciones de todos los planes a la vez.
 *
 * En Firestore los participantes son un mapa id → datos (así se pueden tocar
 * de uno en uno sin pisar al resto); aquí se exponen como lista ordenada.
 */

export type DatosPlan = Pick<Plan, 'nombre' | 'icono' | 'descripcion' | 'fechaInicio' | 'fechaFin' | 'recordatorioDias'>;
export type DatosGasto = Omit<GastoPlan, 'id' | 'planId' | 'creadoPorUid' | 'createdAt' | 'updatedAt'>;
export type DatosPago = Omit<PagoPlan, 'id' | 'planId' | 'creadoPorUid' | 'createdAt' | 'updatedAt'>;

interface AMediasContextType {
  planes: Plan[];
  loadingPlanes: boolean;
  /** Vuelve a pedir la lista de planes (red de seguridad tras unirse a uno). */
  recargarPlanes: () => void;

  /** Crea el plan contigo como primer participante; devuelve su id. */
  crearPlan: (datos: DatosPlan, otrosParticipantes: string[]) => Promise<string>;
  actualizarPlan: (planId: string, datos: Partial<DatosPlan>) => Promise<void>;
  archivarPlan: (planId: string, archivado: boolean) => Promise<void>;
  /** Borra el plan con todos sus gastos y pagos. */
  eliminarPlan: (planId: string) => Promise<void>;

  anadirParticipante: (planId: string, nombre: string) => Promise<void>;
  actualizarParticipante: (planId: string, id: string, datos: Partial<Pick<ParticipantePlan, 'nombre' | 'activo'>>) => Promise<void>;
  /** Solo para quien no tiene movimientos; si los tiene, se desactiva. */
  eliminarParticipante: (planId: string, id: string) => Promise<void>;

  // ── Invitaciones y miembros ──
  /** Crea (o renueva) el enlace de invitación; el anterior deja de valer. */
  abrirInvitacion: (plan: Plan) => Promise<string>;
  cerrarInvitacion: (plan: Plan) => Promise<void>;
  leerInvitacion: (token: string) => Promise<InvitacionPlan | null>;
  /** Te une al plan y, si no lo tenías, te activa A medias. */
  unirseAPlan: (invitacion: InvitacionPlan) => Promise<void>;
  /** Dices quién eres en el plan: un participante sin dueño, o uno nuevo con tu nombre. */
  reclamarParticipante: (planId: string, participanteId: string) => Promise<void>;
  apuntarmeComoNuevo: (planId: string, nombre: string) => Promise<void>;
  cambiarOrganizador: (plan: Plan, uid: string, organiza: boolean) => Promise<void>;
  quitarMiembro: (plan: Plan, uid: string) => Promise<void>;
  salirDelPlan: (plan: Plan) => Promise<void>;

  /** `ticket`: foto ya comprimida (data URL), o nada. */
  crearGasto: (planId: string, g: DatosGasto, ticket?: string) => Promise<void>;
  /** `ticket`: undefined = no tocar la foto; null = quitarla; texto = ponerla. */
  actualizarGasto: (planId: string, gasto: GastoPlan, g: DatosGasto, ticket?: string | null) => Promise<void>;
  eliminarGasto: (planId: string, gasto: GastoPlan) => Promise<void>;
  leerTicket: (planId: string, gastoId: string) => Promise<string | null>;

  crearPago: (planId: string, p: DatosPago) => Promise<void>;
  actualizarPago: (planId: string, id: string, p: DatosPago) => Promise<void>;
  eliminarPago: (planId: string, id: string) => Promise<void>;
}

const AMediasContext = createContext<AMediasContextType | undefined>(undefined);

const fecha = (v: Date | undefined) => (v ? Timestamp.fromDate(new Date(v)) : null);
const nuevoId = () => doc(collection(db, 'planes')).id;

const planDeFirestore = (id: string, d: any): Plan => {
  const mapa: Record<string, any> = d.participantes ?? {};
  const participantes = Object.entries(mapa)
    .map(([pid, p]) => ({
      id: pid,
      nombre: p.nombre ?? '',
      uid: p.uid ?? undefined,
      activo: p.activo !== false,
      orden: p.orden ?? 0,
    }))
    .sort((a, b) => a.orden - b.orden || a.nombre.localeCompare(b.nombre, 'es'))
    .map(({ orden, ...p }) => p);
  return {
    id,
    nombre: d.nombre ?? '',
    icono: d.icono || '🧾',
    descripcion: d.descripcion ?? undefined,
    fechaInicio: d.fechaInicio?.toDate() ?? undefined,
    fechaFin: d.fechaFin?.toDate() ?? undefined,
    moneda: 'EUR',
    creadorUid: d.creadorUid,
    miembrosUids: d.miembrosUids ?? [],
    organizadoresUids: d.organizadoresUids ?? [],
    participantes,
    invitacionToken: d.invitacionToken || undefined,
    // Los planes de antes de existir el recordatorio usan el plazo por defecto
    recordatorioDias: typeof d.recordatorioDias === 'number' ? d.recordatorioDias : PLAZO_POR_DEFECTO,
    archivado: d.archivado === true,
    createdAt: d.createdAt?.toDate() ?? new Date(),
    updatedAt: d.updatedAt?.toDate() ?? d.createdAt?.toDate() ?? new Date(),
  };
};

export const AMediasProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { tieneModulo, perfil, esPlus } = usePerfil();
  // Un listener que las reglas rechazan se muere; al activarse el módulo (por
  // ejemplo, al aceptar una invitación) hay que volver a suscribirse.
  const conModulo = tieneModulo('amedias');
  const [planes, setPlanes] = useState<Plan[]>([]);
  const [recarga, setRecarga] = useState(0);
  const [loadingPlanes, setLoadingPlanes] = useState(true);

  useEffect(() => {
    if (!user || !conModulo) {
      setPlanes([]);
      setLoadingPlanes(false);
      return;
    }
    setLoadingPlanes(true);
    const q = query(collection(db, 'planes'), where('miembrosUids', 'array-contains', user.uid));
    const unsub = onSnapshot(q, snap => {
      const lista = snap.docs.map(d => planDeFirestore(d.id, d.data()));
      // Lo más reciente arriba: por fecha de inicio si la tiene, si no por alta
      const clave = (p: Plan) => (p.fechaInicio ?? p.createdAt).getTime();
      lista.sort((a, b) => clave(b) - clave(a));
      setPlanes(lista);
      setLoadingPlanes(false);
    }, err => {
      // Sin el módulo activado las reglas deniegan la lectura: no es un fallo
      console.warn('A medias no disponible:', err.code);
      setPlanes([]);
      setLoadingPlanes(false);
    });
    return unsub;
  }, [user, conModulo, recarga]);

  const tocarPlan = (planId: string, datos: Record<string, any>) =>
    updateDoc(doc(db, 'planes', planId), { ...datos, updatedAt: Timestamp.now() });

  // ── Planes ───────────────────────────────────────────────────────────────
  const crearPlan: AMediasContextType['crearPlan'] = async (datos, otros) => {
    if (!user) throw new Error('Sesión no iniciada');
    comprobarLimite('planesAMedias', planesOrganizados(planes, user.uid), esPlus);
    const ref = doc(collection(db, 'planes'));
    const participantes: Record<string, any> = {
      [nuevoId()]: { nombre: nombreDeUsuario(user), uid: user.uid, activo: true, orden: 0 },
    };
    otros.map(n => n.trim()).filter(Boolean).forEach((nombre, i) => {
      participantes[nuevoId()] = { nombre, activo: true, orden: i + 1 };
    });
    await setDoc(ref, {
      nombre: datos.nombre,
      icono: datos.icono,
      descripcion: datos.descripcion || null,
      fechaInicio: fecha(datos.fechaInicio),
      fechaFin: fecha(datos.fechaFin),
      moneda: 'EUR',
      creadorUid: user.uid,
      miembrosUids: [user.uid],
      organizadoresUids: [user.uid],
      participantes,
      recordatorioDias: datos.recordatorioDias ?? PLAZO_POR_DEFECTO,
      archivado: false,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    return ref.id;
  };

  const actualizarPlan: AMediasContextType['actualizarPlan'] = async (planId, datos) => {
    const out: Record<string, any> = {};
    if ('nombre' in datos) out.nombre = datos.nombre;
    if ('icono' in datos) out.icono = datos.icono;
    // Como en el resto de módulos: vaciar un opcional es escribir null
    if ('descripcion' in datos) out.descripcion = datos.descripcion || null;
    if ('fechaInicio' in datos) out.fechaInicio = fecha(datos.fechaInicio);
    if ('fechaFin' in datos) out.fechaFin = fecha(datos.fechaFin);
    if ('recordatorioDias' in datos) out.recordatorioDias = datos.recordatorioDias ?? 0;
    await tocarPlan(planId, out);
    // La invitación enseña nombre e icono a quien aún no ve el plan
    const token = planes.find(p => p.id === planId)?.invitacionToken;
    if (token && ('nombre' in datos || 'icono' in datos)) {
      await updateDoc(doc(db, 'invitacionesPlan', token), {
        ...('nombre' in datos ? { nombrePlan: datos.nombre } : {}),
        ...('icono' in datos ? { iconoPlan: datos.icono } : {}),
      });
    }
  };

  const archivarPlan = (planId: string, archivado: boolean) => tocarPlan(planId, { archivado });

  const eliminarPlan = async (planId: string) => {
    // Firestore no borra subcolecciones al borrar el padre: hay que ir a mano.
    // El plan va lo último, porque las reglas de gastos, pagos e invitación lo consultan.
    const token = planes.find(p => p.id === planId)?.invitacionToken;
    if (token) await deleteDoc(doc(db, 'invitacionesPlan', token));
    for (const sub of ['tickets', 'gastos', 'pagos']) {
      const snap = await getDocs(collection(db, 'planes', planId, sub));
      for (let i = 0; i < snap.docs.length; i += 450) {
        const batch = writeBatch(db);
        snap.docs.slice(i, i + 450).forEach(d => batch.delete(d.ref));
        await confirmarLote(batch);
      }
    }
    await deleteDoc(doc(db, 'planes', planId));
  };

  // ── Participantes ────────────────────────────────────────────────────────
  const anadirParticipante = async (planId: string, nombre: string) => {
    // Date.now() como orden: siempre mayor que los de la creación (0, 1, 2…),
    // así el nuevo sale el último sin tener que leer los demás.
    await tocarPlan(planId, {
      [`participantes.${nuevoId()}`]: { nombre: nombre.trim(), activo: true, orden: Date.now() },
    });
  };

  const actualizarParticipante: AMediasContextType['actualizarParticipante'] = async (planId, id, datos) => {
    const out: Record<string, any> = {};
    if (datos.nombre !== undefined) out[`participantes.${id}.nombre`] = datos.nombre.trim();
    if (datos.activo !== undefined) out[`participantes.${id}.activo`] = datos.activo;
    await tocarPlan(planId, out);
  };

  const eliminarParticipante = (planId: string, id: string) =>
    tocarPlan(planId, { [`participantes.${id}`]: deleteField() });

  // ── Invitaciones y miembros ──────────────────────────────────────────────
  const abrirInvitacion = async (plan: Plan) => {
    if (!user) throw new Error('Sesión no iniciada');
    // El id automático de Firestore es aleatorio (20 caracteres): sirve de token
    const ref = doc(collection(db, 'invitacionesPlan'));
    await setDoc(ref, {
      planId: plan.id,
      nombrePlan: plan.nombre,
      iconoPlan: plan.icono,
      creadaPorUid: user.uid,
      creadaPorNombre: plan.participantes.find(p => p.uid === user.uid)?.nombre ?? nombreDeUsuario(user),
      abierta: true,
      createdAt: Timestamp.now(),
    });
    await tocarPlan(plan.id, { invitacionToken: ref.id });
    if (plan.invitacionToken) await deleteDoc(doc(db, 'invitacionesPlan', plan.invitacionToken)).catch(() => {});
    return ref.id;
  };

  const cerrarInvitacion = async (plan: Plan) => {
    await tocarPlan(plan.id, { invitacionToken: null });
    if (plan.invitacionToken) await deleteDoc(doc(db, 'invitacionesPlan', plan.invitacionToken)).catch(() => {});
  };

  const leerInvitacion = async (token: string): Promise<InvitacionPlan | null> => {
    try {
      const snap = await getDoc(doc(db, 'invitacionesPlan', token));
      if (!snap.exists()) return null;
      const d = snap.data();
      return {
        token, planId: d.planId, nombrePlan: d.nombrePlan ?? '', iconoPlan: d.iconoPlan || '🧾',
        creadaPorUid: d.creadaPorUid, creadaPorNombre: d.creadaPorNombre ?? '', abierta: d.abierta === true,
      };
    } catch (e) {
      console.warn('No se pudo leer la invitación', e);
      return null;
    }
  };

  // Un solo batch: o entras al plan con el módulo activado, o no pasa nada.
  // La regla del perfil comprueba, con getAfter, que ya eres miembro.
  const unirseAPlan = async (inv: InvitacionPlan) => {
    if (!user) throw new Error('Sesión no iniciada');
    const batch = writeBatch(db);
    batch.update(doc(db, 'planes', inv.planId), {
      miembrosUids: arrayUnion(user.uid), invitacionUsada: inv.token, updatedAt: Timestamp.now(),
    });
    const yaLoTiene = perfil?.rol === 'admin' || perfil?.modulos?.amedias === true;
    if (!yaLoTiene) {
      batch.update(doc(db, 'usuarios', user.uid), { 'modulos.amedias': true, unidoAPlan: inv.planId });
    }
    await confirmarLote(batch);
  };

  const reclamarParticipante = async (planId: string, participanteId: string) => {
    if (!user) throw new Error('Sesión no iniciada');
    await tocarPlan(planId, { [`participantes.${participanteId}.uid`]: user.uid, reclamado: participanteId });
  };

  const apuntarmeComoNuevo = async (planId: string, nombre: string) => {
    if (!user) throw new Error('Sesión no iniciada');
    const id = nuevoId();
    await tocarPlan(planId, {
      [`participantes.${id}`]: { nombre: nombre.trim(), uid: user.uid, activo: true, orden: Date.now() },
      reclamado: id,
    });
  };

  const cambiarOrganizador = (plan: Plan, uid: string, organiza: boolean) =>
    tocarPlan(plan.id, { organizadoresUids: organiza ? arrayUnion(uid) : arrayRemove(uid) });

  // Quien sale deja de ver el plan, pero su participante y su historial se quedan
  const quitarMiembro = (plan: Plan, uid: string) =>
    tocarPlan(plan.id, { miembrosUids: arrayRemove(uid), organizadoresUids: arrayRemove(uid) });

  const salirDelPlan = async (plan: Plan) => {
    if (!user) throw new Error('Sesión no iniciada');
    await tocarPlan(plan.id, { miembrosUids: arrayRemove(user.uid) });
  };

  // ── Gastos y pagos ───────────────────────────────────────────────────────
  const gastoAFirestore = (g: DatosGasto) => ({
    concepto: g.concepto,
    importe: g.importe,
    fecha: Timestamp.fromDate(new Date(g.fecha)),
    categoria: g.categoria || null,
    pagadoPor: g.pagadoPor,
    reparto: g.reparto,
    importes: g.importes,
  });

  const pagoAFirestore = (p: DatosPago) => ({
    de: p.de,
    a: p.a,
    importe: p.importe,
    fecha: Timestamp.fromDate(new Date(p.fecha)),
    nota: p.nota || null,
  });

  const crearMovimiento = async (planId: string, sub: 'gastos' | 'pagos', datos: Record<string, any>) => {
    if (!user) throw new Error('Sesión no iniciada');
    await setDoc(doc(collection(db, 'planes', planId, sub)), {
      ...datos, creadoPorUid: user.uid, createdAt: Timestamp.now(), updatedAt: Timestamp.now(),
    });
  };

  const actualizarMovimiento = (planId: string, sub: 'gastos' | 'pagos', id: string, datos: Record<string, any>) =>
    // updateDoc y no setDoc: así creadoPorUid se queda como estaba
    updateDoc(doc(db, 'planes', planId, sub, id), { ...datos, updatedAt: Timestamp.now() });

  // ── Tickets ──────────────────────────────────────────────────────────────
  // La foto va en su propio documento (planes/{id}/tickets/{gastoId}) para no
  // cargar imágenes al listar gastos; el gasto solo lleva `conTicket`. Gasto y
  // ticket se escriben en el mismo batch: o los dos o ninguno.
  const refTicket = (planId: string, gastoId: string) => doc(db, 'planes', planId, 'tickets', gastoId);

  const crearGasto: AMediasContextType['crearGasto'] = async (planId, g, ticket) => {
    if (!user) throw new Error('Sesión no iniciada');
    const ref = doc(collection(db, 'planes', planId, 'gastos'));
    const batch = writeBatch(db);
    batch.set(ref, {
      ...gastoAFirestore(g), conTicket: !!ticket,
      creadoPorUid: user.uid, createdAt: Timestamp.now(), updatedAt: Timestamp.now(),
    });
    if (ticket) batch.set(refTicket(planId, ref.id), { imagen: ticket, creadoPorUid: user.uid, createdAt: Timestamp.now() });
    await confirmarLote(batch);
  };

  const actualizarGasto: AMediasContextType['actualizarGasto'] = async (planId, gasto, g, ticket) => {
    if (!user) throw new Error('Sesión no iniciada');
    const batch = writeBatch(db);
    const cambios: Record<string, any> = { ...gastoAFirestore(g), updatedAt: Timestamp.now() };
    if (ticket === null && gasto.conTicket) {
      cambios.conTicket = false;
      batch.delete(refTicket(planId, gasto.id));
    } else if (typeof ticket === 'string') {
      cambios.conTicket = true;
      batch.set(refTicket(planId, gasto.id), { imagen: ticket, creadoPorUid: user.uid, createdAt: Timestamp.now() });
    }
    batch.update(doc(db, 'planes', planId, 'gastos', gasto.id), cambios);
    await confirmarLote(batch);
  };

  const eliminarGasto: AMediasContextType['eliminarGasto'] = async (planId, gasto) => {
    const batch = writeBatch(db);
    if (gasto.conTicket) batch.delete(refTicket(planId, gasto.id));
    batch.delete(doc(db, 'planes', planId, 'gastos', gasto.id));
    await confirmarLote(batch);
  };

  const leerTicket = async (planId: string, gastoId: string) => {
    const snap = await getDoc(refTicket(planId, gastoId));
    return snap.exists() ? (snap.data().imagen as string) : null;
  };

  return (
    <AMediasContext.Provider value={{
      planes, loadingPlanes, recargarPlanes: () => setRecarga(r => r + 1),
      crearPlan, actualizarPlan, archivarPlan, eliminarPlan,
      anadirParticipante, actualizarParticipante, eliminarParticipante,
      abrirInvitacion, cerrarInvitacion, leerInvitacion, unirseAPlan,
      reclamarParticipante, apuntarmeComoNuevo, cambiarOrganizador, quitarMiembro, salirDelPlan,
      crearGasto, actualizarGasto, eliminarGasto, leerTicket,
      crearPago: (planId, p) => crearMovimiento(planId, 'pagos', pagoAFirestore(p)),
      actualizarPago: (planId, id, p) => actualizarMovimiento(planId, 'pagos', id, pagoAFirestore(p)),
      eliminarPago: (planId, id) => deleteDoc(doc(db, 'planes', planId, 'pagos', id)),
    }}>
      {children}
    </AMediasContext.Provider>
  );
};

export const useAMedias = () => {
  const ctx = useContext(AMediasContext);
  if (!ctx) throw new Error('useAMedias debe usarse dentro de AMediasProvider');
  return ctx;
};

/** Gastos y pagos de un plan, en vivo. Lo más reciente primero. */
export function useMovimientosPlan(planId: string | undefined) {
  const [gastos, setGastos] = useState<GastoPlan[]>([]);
  const [pagos, setPagos] = useState<PagoPlan[]>([]);
  const [cargandoGastos, setCargandoGastos] = useState(true);
  const [cargandoPagos, setCargandoPagos] = useState(true);

  useEffect(() => {
    setGastos([]); setPagos([]);
    if (!planId) { setCargandoGastos(false); setCargandoPagos(false); return; }
    setCargandoGastos(true); setCargandoPagos(true);

    const porFecha = <T extends { fecha: Date; createdAt: Date }>(a: T, b: T) =>
      b.fecha.getTime() - a.fecha.getTime() || b.createdAt.getTime() - a.createdAt.getTime();
    const comunes = (d: any) => ({
      planId,
      fecha: d.fecha?.toDate() ?? new Date(),
      creadoPorUid: d.creadoPorUid,
      createdAt: d.createdAt?.toDate() ?? new Date(),
      updatedAt: d.updatedAt?.toDate() ?? d.createdAt?.toDate() ?? new Date(),
    });

    const unsubG = onSnapshot(collection(db, 'planes', planId, 'gastos'), snap => {
      setGastos(snap.docs.map(s => {
        const d = s.data();
        return {
          ...comunes(d),
          id: s.id,
          concepto: d.concepto ?? '',
          importe: d.importe ?? 0,
          categoria: d.categoria ?? undefined,
          pagadoPor: d.pagadoPor ?? {},
          reparto: d.reparto ?? { modo: 'iguales', partes: {} },
          importes: d.importes ?? {},
          conTicket: d.conTicket === true,
        } as GastoPlan;
      }).sort(porFecha));
      setCargandoGastos(false);
    }, err => { console.warn('Gastos del plan no disponibles:', err.code); setCargandoGastos(false); });

    const unsubP = onSnapshot(collection(db, 'planes', planId, 'pagos'), snap => {
      setPagos(snap.docs.map(s => {
        const d = s.data();
        return {
          ...comunes(d),
          id: s.id,
          de: d.de, a: d.a,
          importe: d.importe ?? 0,
          nota: d.nota ?? undefined,
        } as PagoPlan;
      }).sort(porFecha));
      setCargandoPagos(false);
    }, err => { console.warn('Pagos del plan no disponibles:', err.code); setCargandoPagos(false); });

    return () => { unsubG(); unsubP(); };
  }, [planId]);

  return { gastos, pagos, cargando: cargandoGastos || cargandoPagos };
}
